import React, { useState, useEffect } from 'react';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
  UISectionFooter,
} from '@/components/shared/UISectionWrapper';
import UIInput from '@/components/shared/UIInput';
import UIButton from '@/components/shared/UIButton';
import { IUser } from '@/types'; // Assuming IUser is the correct type for the tenant
import './TenantInformationSection.scss';

interface TenantInformationSectionProps {
  tenant: Partial<IUser>;
  onSave?: (data: Partial<IUser>) => void;
  onCancel?: () => void;
}

const TenantInformationSection: React.FC<TenantInformationSectionProps> = ({
  tenant,
  onSave = (data) => console.log('Save tenant info:', data),
  onCancel = () => console.log('Cancel edit'),
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [formData, setFormData] = useState(tenant);

  useEffect(() => {
    setFormData(tenant);
  }, [tenant]);

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleInputChange = (field: keyof IUser) => (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleCancel = () => {
    setFormData(tenant); // Reset form data
    onCancel();
  };

  const CollapseButton = () => (
    <button
      className="TenantInformationSection__collapse-btn"
      onClick={toggleExpanded}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d={isExpanded ? 'M4 6L8 10L12 6' : 'M6 4L10 8L6 12'}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );

  return (
    <UISectionWrapper className="TenantInformationSection">
      <UISectionHeader>
        <div className="TenantInformationSection__header">
          <h3 className="TenantInformationSection__title">Tenant information</h3>
          <CollapseButton />
        </div>
        <div className="TenantInformationSection__divider" />
      </UISectionHeader>

      {isExpanded && (
        <>
          <UISectionBody>
            <div className="TenantInformationSection__form">
              <div className="TenantInformationSection__form-labels">
                <label className="TenantInformationSection__form-label">
                  Full name
                </label>
                <label className="TenantInformationSection__form-label">E-mail</label>
                <label className="TenantInformationSection__form-label">Phone</label>
              </div>

              <div className="TenantInformationSection__form-fields">
                <div className="TenantInformationSection__name-row">
                  <UIInput
                    value={formData.firstname || ''}
                    onChange={handleInputChange('firstname')}
                    placeholder="First Name"
                    className="TenantInformationSection__name-input"
                  />
                  <UIInput
                    value={formData.lastname || ''}
                    onChange={handleInputChange('lastname')}
                    placeholder="Last Name"
                    className="TenantInformationSection__name-input"
                  />
                </div>

                <UIInput
                  value={formData.email || ''}
                  onChange={handleInputChange('email')}
                  placeholder="Email Address"
                  type="email"
                  fullWidth
                />

                <UIInput
                  value={formData.phone || ''}
                  onChange={handleInputChange('phone')}
                  placeholder="Phone Number"
                  type="tel"
                  fullWidth
                />
              </div>
            </div>
          </UISectionBody>

          <UISectionFooter>
            <div className="TenantInformationSection__buttons">
              <UIButton variant="primary" size="medium" onClick={handleSave}>
                Save
              </UIButton>
              <UIButton
                variant="primary"
                outline
                size="medium"
                onClick={handleCancel}
              >
                Cancel
              </UIButton>
            </div>
          </UISectionFooter>
        </>
      )}
    </UISectionWrapper>
  );
};

export default TenantInformationSection;
